import axios from 'axios';
import type { IpInfoData } from '../components/IpInfo';

const apiBase = '/api';

interface Employee {
  id: string;
  name: string;
  recovery_code: string;
}

interface CreateAccountResponse {
  employee: Employee;
  recoveryCode: string;
}

interface CheckinResponse {
  status: 'success' | 'time_restricted' | 'already_checked_in';
  lateStatus: {
    status: 'perfect_on_time' | 'on_time' | 'late_10' | 'late_15';
  };
  mealAllowance: number;
  exemptionApplied: boolean;
  ipInfo?: IpInfoData;
}

export const createAccount = async (name: string): Promise<CreateAccountResponse> => {
  try {
    const response = await axios.post(`${apiBase}/employees/create`, { name });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message || 'Error creating account');
    }
    throw error;
  }
};

export const recoverAccount = async (recoveryCode: string): Promise<Employee> => {
  try {
    const response = await axios.post(`${apiBase}/employees/recover`, { recoveryCode: recoveryCode });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message || 'Error recovering account');
    }
    throw error;
  }
};

export const checkIpBeforeCheckin = async (showConfirmation = true): Promise<{ confirmed: boolean; ipInfo: IpInfoData }> => {
  try {
    const response = await axios.get(`${apiBase}/ip-info`);
    const ipInfo = response.data;

    if (!ipInfo.isShopWifi && showConfirmation) {
      const confirmed = window.confirm(
        'Warning: You are not connected to shop WiFi. Are you sure you want to proceed with check-in?'
      );
      return { confirmed, ipInfo };
    }

    return { confirmed: true, ipInfo };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message || 'Error checking IP');
    }
    throw error;
  }
};

export const recordCheckin = async (employeeId: string): Promise<CheckinResponse> => {
  try {
    const response = await axios.post(`${apiBase}/checkins/record`, { employeeId: employeeId });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message || 'Error recording check-in');
    }
    throw error;
  }
};

export const getEmployeeCheckins = async (employeeId: string) => {
  try {
    const response = await axios.get(`${apiBase}/checkins/history?employeeId=${employeeId}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message || 'Error fetching check-ins');
    }
    throw error;
  }
}; 